import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { MetadataEntityData, MetadataEntityFieldData, MetadataEntityFieldDomain, ResolvedViewGroupHeader } from '@jms/model';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { WbValueComponent } from '@workbuddy/ui/v2/components/primitives/value';
import { groupHeaderBridgeStyles, groupHeaderStyleId } from './group.header.theme';

/**
 * The one group-header phrase: title · elevated fields · count.
 *
 * Both hosts render this, so a Job reads identically whether it heads an AG Grid group row on the
 * table or a swimlane on the kanban. Each host keeps only what is genuinely its own — the table's
 * group actions and expand click, the kanban's chevron and tap-to-open — and neither owns the words.
 *
 * Values come from ONE record rather than the group key, because the header names the thing the
 * group is (a job number wants its customer and site beside it) and those fields ride on every row
 * of that group. A group with no elevated defaults renders title + count and reads as it always did.
 */
@Component({
  selector: 'board-group-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [WbValueComponent],
  styleUrls: ['./group.header.component.less'],
  templateUrl: './group.header.component.html',
})
export class GroupHeaderComponent {

  private readonly document = inject(DOCUMENT);

  /** The resolved header definition (`resolveViewGroupHeader`); null renders the fallback title only. */
  public readonly $header = input<ResolvedViewGroupHeader | null>(null, { alias: 'header' });
  /** One record of the group — every field the header elevates is read off it. */
  public readonly $record = input<any>(null, { alias: 'record' });
  public readonly $metadata = input<MetadataEntityData | null>(null, { alias: 'metadata' });
  public readonly $count = input<number | null>(null, { alias: 'count' });
  /**
   * The rendered count phrase — '4', '4 stages', '20 of 4,312 stages'.
   *
   * The hosts word the count differently (the table shows a bare number beside the title, the kanban
   * a noun phrase with the tail's total), so the phrase arrives finished rather than being built here
   * from `count`. `count` still gates whether it paints at all.
   */
  public readonly $countLabel = input<string>('', { alias: 'countLabel' });
  /**
   * The group's own name, used when the record carries nothing at the title path.
   *
   * The no-value group has no record value to read, and a group on a virtual field stores nothing at
   * its own path — both would render 'Not Set' where the group's name is the truth.
   */
  public readonly $groupName = input<string>('', { alias: 'groupName' });

  private readonly $uiFields = computed<MetadataEntityFieldData[]>(() => {

    const metadata = this.$metadata();
    return metadata ? Object.values(new MetadataEntityFieldDomain(metadata).uiFields) : [];
  });

  protected readonly $titleField = computed(() => findField(this.$uiFields(), this.$header()?.titleField));

  /**
   * Set only when the record carries nothing at the title path — `wb-value` prefers an explicit
   * value, and a stored field is richer than its key (a status renders as a pill, its key is a bare string).
   */
  protected readonly $titleValue = computed(() => {

    const titleField = this.$header()?.titleField;
    const stored = titleField ? getValueFromPath(this.$record(), titleField) : null;
    return stored == null ? this.$groupName() || null : null;
  });

  protected readonly $elevatedFields = computed<MetadataEntityFieldData[]>(() => {

    const paths = this.$header()?.fields ?? [];
    return paths.map(path => findField(this.$uiFields(), path)).filter((field): field is MetadataEntityFieldData => !!field);
  });

  /** The count paints only when the header asks for one and there is something to count. */
  protected readonly $showCount = computed(() => !!this.$header()?.showCount && !!this.$countLabel() && (this.$count() ?? 0) > 0);

  public constructor() {

    DesignSystem.Mount(this.document, groupHeaderStyleId, groupHeaderBridgeStyles);
  }
}

/**
 * The metadata field for a path, or a stub carrying the path so `wb-value` can still stringify it.
 *
 * A group column fed by a valueGetter has no domain field; dropping it would silently lose the
 * elevated value rather than showing the raw one.
 */
function findField(fields: readonly MetadataEntityFieldData[], path: string | null | undefined): MetadataEntityFieldData | null {

  if (!path) return null;

  const direct = fields.find(field => field.path === path || field.name === path || (field as { _id?: string })._id === path);
  return direct ?? { name: path, path, displayName: path } as MetadataEntityFieldData;
}

function getValueFromPath(record: unknown, path: string): unknown {

  if (!record || !path) return null;

  return path.split('.').reduce((value: any, part) => value?.[part], record as any);
}
